import Button from "@/components/Button";
import { InputField } from "@/components/InputField";
import { CloseIcon } from "@/components/features/SquadBuilder/RightPanel/PanelHeader/PanelHeader";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useEvolutionPickerSelectionLabel } from "@/pages/EvolutionsHub/EvolutionsOverviewPage/EvolutionsOverviewPage";
import { EvolutionsPickerSidebar } from "@/pages/EvolutionsHub/EvolutionsPickerSidebar";
import { evolutionFiltersSelector } from "@/redux/evolutions/evolutionsBuilderSlice";
import {
  evolutionCategoriesOrderedSelector,
  evolutionsSelector,
} from "@/redux/evolutions/evolutionsSlice";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { BREAKPOINTS, SKIP_RESET_EVOLUTIONS_DEFAULT_MODAL } from "@/settings";
import {
  type EvolutionPickerSelection,
  availableEvolutionIds,
} from "@repo/shared/evolutions";
import { useEvolutionPicker } from "@repo/shared/hooks";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { ChevronRight, Dna, PanelLeft, SquareCheck, X } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { EvolutionFilterCard } from "./EvolutionFilterCard";
import { FilterSidebarItem } from "./FilterSidebarItem";

export type EvolutionFilterData = {
  includedEvolutions: number[];
  mustIncludeEvolutions: number[];
};

interface Props {
  onClose: () => void;
  onApply?: (data: EvolutionFilterData) => void;
  initialIncludedEvolutions?: number[];
  initialMustIncludeEvolutions?: number[];
  activeFilter: EvolutionPickerSelection;
  handleSelectActiveFilter: (filter: EvolutionPickerSelection) => void;
  showExpired?: boolean;
  ignoreExcludedByDefault?: boolean;
}

/**
 * The include/exclude evolutions panel.
 *
 * Every selection rule — what each panel entry shows, how a draft toggle moves
 * between include / must-include / exclude, when Apply and Reset are live —
 * lives in `useEvolutionPicker` and the pure functions behind it, shared with
 * the mobile app. What is left here is layout: a sidebar-and-grid on desktop,
 * a single column with a slide-over sidebar on touch devices.
 */
export const IncludeExcludeEvolutions = forwardRef<HTMLDivElement, Props>(
  (
    {
      onApply,
      onClose,
      initialIncludedEvolutions,
      initialMustIncludeEvolutions,
      activeFilter,
      handleSelectActiveFilter,
      showExpired,
      ignoreExcludedByDefault,
    },
    ref,
  ) => {
    const { t } = useTranslation(["evolutions", "common"]);
    const dispatch = useDispatch<AppDispatch>();
    const { evolutions, expiredEvolutions } = useSelector(evolutionsSelector);
    const orderedCategories = useSelector(evolutionCategoriesOrderedSelector);
    const {
      includedEvolutions: builderIncludedEvolutions,
      mustIncludeEvolutions: builderMustIncludeEvolutions,
    } = useSelector(evolutionFiltersSelector);
    const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);

    // Hosted from the players page the caller passes its own selection; hosted
    // from the builder it falls back to the builder's, and to "everything
    // available" the first time the builder is opened.
    const allAvailableIds = useMemo(
      () => availableEvolutionIds(evolutions, ignoreExcludedByDefault),
      [evolutions, ignoreExcludedByDefault],
    );
    const initialIncluded =
      initialIncludedEvolutions ??
      (builderIncludedEvolutions.length > 0
        ? builderIncludedEvolutions
        : allAvailableIds);
    const initialMustInclude =
      initialMustIncludeEvolutions ?? builderMustIncludeEvolutions;

    const now = Date.now();
    const picker = useEvolutionPicker({
      evolutions,
      expiredEvolutions,
      orderedCategories,
      initialIncluded,
      initialMustInclude,
      selection: activeFilter,
      onSelectionChange: handleSelectActiveFilter,
      now,
      showExpired,
      ignoreExcludedByDefault,
    });
    const selectionLabel = useEvolutionPickerSelectionLabel(
      activeFilter,
      orderedCategories,
    );

    const [showMobileFilters, setShowMobileFilters] = useState(false);

    // Applying and dismissing both suppress the unmount auto-apply below.
    const skipUnmountApplyRef = useRef(false);
    const draftRef = useRef(picker.draft);
    draftRef.current = picker.draft;
    const hasChangesRef = useRef(picker.hasChanges);
    hasChangesRef.current = picker.hasChanges;
    const onApplyRef = useRef(onApply);
    onApplyRef.current = onApply;

    const applyDraft = useCallback(() => {
      skipUnmountApplyRef.current = true;
      onApplyRef.current?.({
        includedEvolutions: draftRef.current.included,
        mustIncludeEvolutions: draftRef.current.mustInclude,
      });
    }, []);

    const handleDismiss = () => {
      skipUnmountApplyRef.current = true;
      onClose();
    };

    // Closing the panel by any other route (navigating away, an outside click)
    // still commits — the panel has no explicit cancel.
    useEffect(
      () => () => {
        if (skipUnmountApplyRef.current || !hasChangesRef.current) return;
        applyDraft();
      },
      [applyDraft],
    );

    const handleSelectFilter = (filter: EvolutionPickerSelection) => {
      picker.selectSelection(filter);
      setShowMobileFilters(false);
    };

    const performReset = useCallback(() => picker.reset(), [picker]);

    const requestReset = useCallback(() => {
      if (
        localStorage.getItem(SKIP_RESET_EVOLUTIONS_DEFAULT_MODAL) === "true"
      ) {
        performReset();
        return;
      }
      dispatch(
        showModal({
          modalType: "reset-evolutions-default",
          data: { onConfirm: performReset },
        }),
      );
    }, [dispatch, performReset]);

    const renderCard = (
      evolution: (typeof picker.visibleEvolutions)[number],
    ) => (
      <EvolutionFilterCard
        key={`${evolution.id}-${evolution.categoryId}`}
        evolution={evolution}
        filterState={picker.stateOf(evolution.id)}
        onInclude={() => picker.include(evolution.id)}
        onExclude={() => picker.exclude(evolution.id)}
        onMustInclude={() => picker.mustInclude(evolution.id)}
        isExpired={picker.isExpired(evolution.id)}
      />
    );

    const bulkActions = (
      <div className="flex items-center gap-2">
        {activeFilter.type !== "all-included" && (
          <Button
            variant="tertiary"
            label={t("picker.includeAll")}
            onClick={picker.includeAllVisible}
          />
        )}
        <Button
          variant="tertiary"
          label={t("picker.excludeAll")}
          onClick={picker.excludeAllVisible}
        />
      </div>
    );

    const sidebarHeaderContent = (
      <FilterSidebarItem
        label={t("picker.allIncluded")}
        count={`(${picker.includedCount}/${picker.totalCount})`}
        icon={<SquareCheck size={20} className="text-dark-tertiary" />}
        isActive={activeFilter.type === "all-included"}
        onClick={() => handleSelectFilter({ type: "all-included" })}
      />
    );

    return !isTouchDevice ? (
      <div className="flex flex-col h-[85vh]" ref={ref}>
        <div className="px-5 py-5 border-b border-panel-foreground ">
          <div className="flex flex-col gap-y-4">
            <div className="flex items-center justify-between ">
              <div className="flex items-center gap-2">
                <Dna size={24} className="text-dark-tertiary" />
                <span className="text-xl font-bold text-dark-secondary">
                  {t("picker.title")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-14">
                  <Button
                    label={t("common:reset")}
                    variant={picker.isAtDefault ? "disabled" : "tertiary"}
                    large
                    disabled={picker.isAtDefault}
                    onClick={requestReset}
                  />
                </div>
                <div className="w-34">
                  <Button
                    variant={picker.hasChanges ? "primary" : "disabled"}
                    label={t("picker.applyChanges")}
                    onClick={applyDraft}
                    disabled={!picker.hasChanges}
                    large
                  />
                </div>
                <button
                  className="bg-panel-foreground rounded size-9 cursor-pointer hover:bg-panel-foreground/80 transition-colors flex items-center justify-center shrink-0"
                  onClick={handleDismiss}
                >
                  <X size={16} className="text-dark-secondary" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          <EvolutionsPickerSidebar
            activeFilter={activeFilter}
            onFilterChange={handleSelectFilter}
            searchTerm={picker.searchTerm}
            onSearchChange={picker.setSearchTerm}
            headerContent={sidebarHeaderContent}
            showExpired={showExpired}
          />

          <ScrollArea className="flex-1 [&>[data-radix-scroll-area-viewport]>div]:block!">
            <div className="flex flex-col gap-6 p-6">
              <div className="flex flex-col items-start justify-between gap-4">
                <span className="text-lg font-bold text-dark-secondary">
                  {selectionLabel}
                </span>
                {bulkActions}
              </div>
              {picker.searchTerm.trim() ? (
                picker.groupedEvolutions.map(({ category, evolutions }) => (
                  <div key={category.id} className="flex flex-col gap-3">
                    <span className="text-base font-medium text-dark-tertiary">
                      {category.name}{" "}
                      <span className="text-dark-quaternary">
                        ({evolutions.length})
                      </span>
                    </span>
                    <div className="flex flex-wrap gap-4">
                      {evolutions.map(renderCard)}
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-wrap gap-4">
                  {picker.visibleEvolutions.length === 0 ? (
                    activeFilter.type === "all-included" ? (
                      <span className="text-dark-secondary mx-auto">
                        {t("picker.emptyAllIncluded")}
                      </span>
                    ) : activeFilter.type === "state" ? (
                      <div className="flex flex-col items-center justify-center mx-auto gap-4 text-dark-tertiary">
                        <span>
                          {t("picker.emptyState", {
                            label: selectionLabel,
                          })}
                        </span>
                        <span>
                          <Trans
                            ns="evolutions"
                            i18nKey="picker.manageHint"
                            components={{
                              textLink: (
                                <NavLink
                                  to="/evolutions"
                                  className="underline text-dark-link"
                                />
                              ),
                            }}
                          />
                        </span>
                      </div>
                    ) : (
                      <span className="text-dark-secondary mx-auto">
                        {t("picker.empty")}
                      </span>
                    )
                  ) : (
                    picker.visibleEvolutions.map(renderCard)
                  )}
                </div>
              )}
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
        </div>
      </div>
    ) : (
      <div className="relative h-full" ref={ref}>
        <div
          className={cn(
            "inset-0 z-30 bg-panel-background transition-transform duration-200 fixed top-0 left-0 right-0 bottom-0",
            showMobileFilters ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-5 py-4 shrink-0">
              <div />
              <button
                className="p-4 -m-4 cursor-pointer"
                onClick={() => setShowMobileFilters(false)}
              >
                <CloseIcon />
              </button>
            </div>
            <div className="flex-1 min-h-0">
              <EvolutionsPickerSidebar
                activeFilter={activeFilter}
                onFilterChange={handleSelectFilter}
                searchTerm={picker.searchTerm}
                headerContent={sidebarHeaderContent}
                className="w-full border-r-0"
                showExpired={showExpired}
              />
            </div>
          </div>
        </div>

        <ScrollArea className="ml-auto w-screen bg-inner-box desktop:w-[352px] h-full [&>[data-radix-scroll-area-viewport]>div]:block!">
          <div className="flex flex-col gap-y-3 pb-32 desktop:pb-30 flex-1">
            <ScrollBar
              orientation="vertical"
              className="cursor-grab"
              forceMount={true}
              scrollBarColor="bg-white/50"
            />

            <div className="sticky top-0 z-10 bg-inner-box px-4 py-3 flex flex-row gap-x-2">
              <div className="flex-1">
                <Button
                  label={t("common:reset")}
                  large
                  variant={picker.isAtDefault ? "disabled" : "tertiary"}
                  onClick={requestReset}
                />
              </div>
              <div className="flex-1">
                <Button
                  variant={picker.hasChanges ? "primary" : "disabled"}
                  label={t("picker.applyChanges")}
                  large
                  onClick={applyDraft}
                  disabled={!picker.hasChanges}
                />
              </div>
            </div>

            <div className="flex flex-col gap-y-2.5 px-4 -mt-2">
              <button
                className="flex items-center gap-2 rounded-lg px-3 py-2 bg-panel-middleground w-full cursor-pointer active:bg-panel-foreground transition-colors border border-white/10"
                onClick={() => setShowMobileFilters(true)}
              >
                <PanelLeft size={18} className="text-dark-tertiary shrink-0" />
                <span className="text-base font-medium text-dark-secondary flex-1 text-left">
                  {selectionLabel}
                </span>
                <ChevronRight
                  size={16}
                  className="text-dark-tertiary shrink-0"
                />
              </button>
              <div className="mt-2">
                <InputField
                  value={picker.searchTerm}
                  onChange={picker.setSearchTerm}
                  dismissSearch={() => picker.setSearchTerm("")}
                  placeholder={t("picker.searchPlaceholder")}
                  type={"search"}
                  showSearch={true}
                />
              </div>
            </div>

            <div className="px-4">{bulkActions}</div>

            <div className="flex flex-col gap-y-2 px-4">
              {picker.visibleEvolutions.map(renderCard)}
            </div>
          </div>
        </ScrollArea>
      </div>
    );
  },
);
